// pms-adapters / channelOps — `ChannelOpsService` over the PMS sync Function.
//
// The one adapter in this package that talks to OUR OWN backend rather than to
// Postgres or to Channex. It exists because of where the Channex API key lives:
// the CF Pages env, and nowhere else. The browser holds a Postgres session and
// no vendor credential (functions/api/pms/provision.ts's header), so a screen
// cannot run a sync — it can only ASK for one. This is that ask, expressed as
// the `ChannelOpsService` port so `ChannexMapping.tsx` never names a URL, a
// method or a token.
//
// ── What it sends, and what it deliberately does not ──────────────────────
//
// It sends the caller's OWN access token, read at call time. Not the anon key,
// not a service key, and not a token captured when the services were built —
// a PMS session outlives a JWT's lifetime, so a captured one starts answering
// 401 mid-afternoon. The Function verifies it (JWKS) and then checks
// `pms.ops.write` against `my_capabilities()`, which is the same row set the
// gate below reads. Two gates, one source of truth, neither trusting the other.

import { CAPABILITIES, type ActorSource } from '@lemontaigu/platform';
import { err, ok } from '@lemontaigu/pms-core';
import type {
  ChannelOpsService,
  PropertyCode,
  Result,
  SweepReport,
  SyncReport,
} from '@lemontaigu/pms-core';
import { gate } from './services.js';

/** How the composition root reaches the Function and the caller's session. */
export interface ChannelOpsConfig {
  /** Origin serving the PMS Functions. They deploy with the SITE CF Pages
   *  project, not this SPA's — the reception `CHAT_API_BASE` pattern. */
  apiBase: string;
  /** The caller's own access token, READ AT CALL TIME (see the header). */
  accessToken(): Promise<string | null>;
}

export class HttpChannelOpsService implements ChannelOpsService {
  constructor(
    private readonly cfg: ChannelOpsConfig,
    private readonly actor: ActorSource,
    // Wrapped rather than passed bare, for the reason the Channex adapter
    // records: `fetch` is a global whose `this` some runtimes check.
    private readonly fetchImpl: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  syncNow(propertyCode: PropertyCode): Promise<Result<SyncReport>> {
    return this.post<SyncReport>('/api/pms/sync', propertyCode, (b) => Array.isArray(b.roomTypes));
  }

  sweepNow(propertyCode: PropertyCode): Promise<Result<SweepReport>> {
    return this.post<SweepReport>('/api/pms/sweep', propertyCode, (b) => Array.isArray(b.revisions));
  }

  /**
   * The one place this package speaks HTTP to our own backend.
   *
   * `hasReport` is passed per endpoint because BOTH answer 502 with a complete
   * report when something failed — a sync with a refused category, a sweep with
   * a booking it could not apply. Treating a non-2xx as « the call failed »
   * would throw away the part that says what DID land, which on a sync of six
   * categories is five of them.
   */
  private async post<T>(
    path: string,
    propertyCode: PropertyCode,
    hasReport: (body: Record<string, unknown>) => boolean,
  ): Promise<Result<T>> {
    // L2. The endpoint gates again at L4 — this one exists so a receptionist
    // without `pms.ops.write` reads a French sentence instead of a 403.
    const denied = gate(this.actor, CAPABILITIES.PMS_OPS_WRITE);
    if (denied) return denied;
    if (!propertyCode) {
      return err('validation', 'Aucun établissement sélectionné — opération impossible');
    }

    const token = await this.cfg.accessToken();
    if (!token) return err('forbidden', 'Session expirée — reconnectez-vous.', { retryable: false });

    let res: Response;
    try {
      res = await this.fetchImpl(`${this.cfg.apiBase}${path}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ propertyCode }),
      });
    } catch (e) {
      const why = e instanceof Error ? `${e.name}: ${e.message}` : String(e);
      return err('transient', `Service injoignable — ${this.cfg.apiBase} (${why})`, {
        retryable: true,
        cause: e,
      });
    }

    const body = (await res.json().catch(() => null)) as (Record<string, unknown> & { error?: string }) | null;

    if (!body || !hasReport(body)) {
      if (!res.ok) {
        return err(
          res.status === 401 || res.status === 403 ? 'forbidden' : 'integration',
          body?.error ?? `Opération refusée (${res.status})`,
          { retryable: res.status >= 500, cause: body },
        );
      }
      return err('integration', 'Réponse illisible', { cause: body });
    }
    return ok(body as T);
  }
}

/**
 * The null object for a composition root that was not given an API base.
 *
 * Not an error thrown at build time, and not a silent no-op either: an app that
 * never wires the sync (a test harness, a future embedding) still gets a
 * complete `PmsServices`, and the one screen that offers the button gets a
 * sentence explaining why it cannot. A no-op returning `ok` would show a green
 * « synchronisé » for a sync that never left the browser.
 */
export class UnavailableChannelOpsService implements ChannelOpsService {
  private refuse<T>(): Promise<Result<T>> {
    return Promise.resolve(
      err('validation', "Les opérations Channex ne sont pas configurées dans cette application", {
        retryable: false,
      }),
    );
  }
  syncNow(): Promise<Result<SyncReport>> {
    return this.refuse<SyncReport>();
  }
  sweepNow(): Promise<Result<SweepReport>> {
    return this.refuse<SweepReport>();
  }
}
